import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getOffer, initOfferOrder, initializeOfferPayment } from '../../services/offers'
import { isLogged } from '../../services/helpers'
import MobileBottomNav from '../../components/MobileBottomNav'

const OfferDetailPage = () => {
  const { offerId } = useParams()
  const navigate = useNavigate()
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [buying, setBuying] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const res = await getOffer(offerId)
      if (res.status === 200) {
        setOffer(res.data)
      }
      setLoading(false)
    }
    load()
  }, [offerId])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading offer...</p>
        </div>
      </div>
    )
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-900 mb-2">Offer not found</p>
          <button
            onClick={() => navigate('/offers')}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            ← Back to offers
          </button>
        </div>
      </div>
    )
  }

  const normalPrice = offer.normalPrice || 0
  const discountedPrice = offer.discountedPrice || 0
  const discount = normalPrice > 0 ? Math.round(((normalPrice - discountedPrice) / normalPrice) * 100) : 0
  const remaining = offer.remaining || 0
  const isExpired = offer.validUntil && new Date(offer.validUntil) < new Date()
  const isAvailable = remaining > 0 && !isExpired

  // Calculate time left
  const getTimeLeft = () => {
    if (!offer.validUntil || isExpired) return null
    const now = new Date()
    const validUntil = new Date(offer.validUntil)
    const diffMs = validUntil - now
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffMs <= 0) return null
    if (diffDays > 0) return { value: diffDays, unit: 'days' }
    if (diffHours > 0) return { value: diffHours, unit: 'hours' }
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    return { value: diffMinutes, unit: 'minutes' }
  }

  const timeLeft = getTimeLeft()
  const isUrgent = timeLeft && timeLeft.unit === 'hours' && timeLeft.value <= 24

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: offer.currency || 'NGN',
      minimumFractionDigits: 0
    }).format(price)
  }

  const handleQuantityChange = (change) => {
    const newQty = Math.max(1, Math.min(remaining, quantity + change))
    setQuantity(newQty)
  }

  const handlePurchase = async () => {
    if (!isLogged()) {
      sessionStorage.setItem('pendingOfferPurchase', JSON.stringify({ offerId, quantity }))
      navigate('/login', { state: { from: `/offers/${offerId}` } })
      return
    }

    if (quantity < 1 || quantity > remaining) {
      setMessage(`Please select a quantity between 1 and ${remaining}`)
      setMessageType('error')
      return
    }

    setMessage('')
    setMessageType('info')
    setBuying(true)

    try {
      // Initialize order
      const orderRes = await initOfferOrder(offerId, quantity)

      if ((orderRes.status !== 200 && orderRes.status !== 201) || !orderRes.data) {
        const errorMsg = orderRes.error || 'Failed to initialize order. Please try again.'
        setMessage(errorMsg)
        setMessageType('error')
        setBuying(false)
        return
      }

      const order = orderRes.data

      // Initialize payment
      const redirectUrl = `${window.location.origin}/payment/success?orderId=${order._id}&offerId=${offerId}`
      const paymentRes = await initializeOfferPayment(order._id, redirectUrl)

      if (paymentRes.status !== 200 || !paymentRes.data) {
        const errorMsg = paymentRes.error || 'Failed to initialize payment. Please try again.'
        setMessage(errorMsg)
        setMessageType('error')
        setBuying(false)
        return
      }

      // Redirect to payment link
      const paymentLink = paymentRes.data.paymentLink || paymentRes.data.link
      if (paymentLink) {
        window.location.href = paymentLink
      } else {
        setMessage('Payment link not available. Please try again.')
        setMessageType('error')
        setBuying(false)
      }
    } catch (err) {
      console.error('Purchase error:', err)
      setMessage('Unable to start payment. Please try again.')
      setMessageType('error')
      setBuying(false)
    }
  }

  const images = offer.images && offer.images.length > 0 ? offer.images : []

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white pb-20 lg:pb-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => {
            navigate('/', { state: { scrollToOffers: true } })
            // Scroll to offers section after navigation
            setTimeout(() => {
              const offersSection = document.getElementById('offers')
              if (offersSection) {
                offersSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }, 200)
          }}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to offers
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images Section */}
          <div>
            {images.length > 0 ? (
              <div>
                <div className="relative h-96 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl overflow-hidden mb-4">
                  <img
                    src={images[selectedImage]}
                    alt={offer.title}
                    className="w-full h-full object-cover"
                  />
                  {discount > 0 && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      {discount}% OFF
                    </div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImage === idx ? 'border-emerald-500' : 'border-transparent'
                        }`}
                      >
                        <img src={img} alt={`${offer.title} ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="h-96 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl flex items-center justify-center">
                <svg className="w-24 h-24 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
            )}
          </div>

          {/* Details Section */}
          <div>
            {offer.category && (
              <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-2">
                {offer.category}
              </p>
            )}
            
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {offer.title}
            </h1>

            {/* Center Info */}
            {offer.centerId && (
              <div className="flex items-center gap-2 text-slate-600 mb-6 pb-6 border-b border-slate-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="font-medium">{offer.centerId.organization || 'Unknown Center'}</span>
              </div>
            )}

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold text-slate-900">
                  {formatPrice(discountedPrice)}
                </span>
                {normalPrice > discountedPrice && (
                  <span className="text-xl text-slate-400 line-through">
                    {formatPrice(normalPrice)}
                  </span>
                )}
              </div>
              {discount > 0 && (
                <p className="text-sm text-green-600 font-medium">
                  You save {formatPrice(normalPrice - discountedPrice)} ({discount}% off)
                </p>
              )}
            </div>

            {/* Availability - Time Left & Tables Left */}
            {isAvailable && (
              <div className="space-y-3 mb-6">
                {/* Time Left */}
                {timeLeft && (
                  <div className={`rounded-lg p-4 border-2 ${
                    isUrgent ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className={`text-sm font-semibold ${isUrgent ? 'text-red-700' : 'text-slate-700'}`}>
                          Time Left:
                        </span>
                      </div>
                      <span className={`text-lg font-bold ${isUrgent ? 'text-red-700' : 'text-slate-900'}`}>
                        {timeLeft.value} {timeLeft.unit} left
                      </span>
                    </div>
                  </div>
                )}

                {/* Tables Left */}
                <div className={`rounded-lg p-4 border-2 ${
                  remaining <= 5 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className={`w-5 h-5 ${remaining <= 5 ? 'text-orange-600' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className={`text-sm font-semibold ${remaining <= 5 ? 'text-orange-700' : 'text-slate-700'}`}>
                        {remaining === 1 ? 'Only 1 table left!' : `${remaining} tables left`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quantity Selector */}
            {isAvailable && (
              <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Quantity:</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1 || buying}
                      className="w-10 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold"
                    >
                      −
                    </button>
                    <span className="w-12 text-center text-lg font-semibold text-slate-900">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= remaining || buying}
                      className="w-10 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Total:</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {formatPrice(discountedPrice * quantity)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Message */}
            {message && (
              <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                messageType === 'error' 
                  ? 'border-red-200 bg-red-50 text-red-800' 
                  : messageType === 'success'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-emerald-100 bg-emerald-50 text-emerald-800'
              }`}>
                {message}
              </div>
            )}

            {/* Purchase Button */}
            {isAvailable ? (
              <button
                onClick={handlePurchase}
                disabled={buying}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {buying ? 'Processing...' : `Get This Offer (${quantity})`}
              </button>
            ) : (
              <div className="w-full bg-slate-200 text-slate-600 font-semibold py-4 px-6 rounded-lg text-center">
                {isExpired ? 'Offer Expired' : 'Sold Out'}
              </div>
            )}

            {/* Description */}
            <div className="mt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3">About this offer</h2>
              <p className="text-slate-700 whitespace-pre-line leading-relaxed">
                {offer.description}
              </p>
            </div>

            {/* Terms */}
            {offer.terms && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Terms & Conditions</h3>
                <p className="text-sm text-slate-600 whitespace-pre-line">
                  {offer.terms}
                </p>
              </div>
            )}

            {/* Tags */}
            {offer.tags && offer.tags.length > 0 && (
              <div className="mt-6">
                <div className="flex flex-wrap gap-2">
                  {offer.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  )
}

export default OfferDetailPage
